import { useMemo, useRef } from "react";
import {
  useContentHubSearch,
  flattenContentHubPages,
  type ContentHubFilter,
  type ContentHubItem,
} from "@/features/content-hub";
import type { NewsFeedItem } from "@/shared/api/firehose/models";
import { mapContentHubItemToStory } from "./useContentHubFirehoseFeed";

const getCreatedMs = (story: NewsFeedItem): number =>
  Date.parse(story.created_utc || "");

interface Options {
  /** Gate the query — when false nothing is fetched. */
  enabled?: boolean;
  /** Per-widget mapper to the legacy story model. Defaults to the shared
   *  source-dispatched mapper; truth-social passes its own. */
  mapItem?: (item: ContentHubItem) => NewsFeedItem;
}

/**
 * Content-hub-backed filtered search for the firehose-style widgets (fin-x,
 * truth-social, stocks-news). Compiles the generalized `ContentHubFilter` to a
 * filter tree, hits POST /v1/search, and adapts the results to the legacy
 * `NewsFeedItem` model the firehose rows render — so the panel's tuple/highlight
 * contract is unchanged.
 *
 * The pinned source(s) travel inside `filter.sources` (single-source widgets
 * pass one, stocks-news passes the whole group), so no `pinnedSource` is used.
 * Cursor-paginated — the single merged cursor pages the whole filtered result,
 * across sources.
 */
export function useContentHubFirehoseSearch(
  filter: ContentHubFilter,
  { enabled = true, mapItem = mapContentHubItemToStory }: Options = {}
) {
  const query = useContentHubSearch(filter, { enabled });

  // Stable story identity across refetches — the row component mutates
  // show_deatils on the object, so remapping the same item must return it.
  const mappedCacheRef = useRef(new WeakMap<ContentHubItem, NewsFeedItem>());
  const mapItemRef = useRef(mapItem);
  mapItemRef.current = mapItem;

  const stories = useMemo(() => {
    return flattenContentHubPages(query.data)
      .map((item): NewsFeedItem => {
        const cached = mappedCacheRef.current.get(item);
        if (cached) return cached;
        const story = mapItemRef.current(item);
        mappedCacheRef.current.set(item, story);
        return story;
      })
      .sort((a, b) => getCreatedMs(b) - getCreatedMs(a));
  }, [query.data]);

  return {
    stories,
    isLoading: query.isPending && enabled,
    isError: query.isError,
    error: query.error as Error | null,
    hasMore: query.hasNextPage,
    loadNextPage: query.fetchNextPage,
    loadingNextPage: query.isFetchingNextPage,
  };
}
